import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../fonts/breuer-headline.css";
import "./thankyou.css";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";
import { pledgeDisplayText } from "./pledgeTexts";

import thankYouBg from "./img/thankyoubg.png";
import thankYouTitle from "./img/ThankYou.png";
import likeOnImg from "./img/like-on.png";
import likeOffImg from "./img/like-off.png";
import popupLikeImg from "./img/popup-like.png";
import ohnoImg from "./img/ohno.png";

const PLEDGE_ICONS = {
  1: "🌱",
  2: "♻️",
  3: "💧",
  4: "🌍",
  5: "🌳",
  6: "✨",
};

const FEEDBACK_OPTIONS = [
  { value: "Amazingggg", emoji: "🤩", label: "Amazingggg" },
  { value: "Loved it", emoji: "😍", label: "Loved it" },
  { value: "It was fun", emoji: "😊", label: "It was fun" },
  { value: "It was okay", emoji: "👍", label: "It was okay" },
];

const CONFETTI_MS = 2000;
const CONFETTI_COUNT = 64;

function buildEmojiConfettiPieces() {
  const ts = Date.now();
  return Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    key: `${ts}-${i}`,
    x: Math.random() * 92 + 4,
    y: Math.random() * 85 + 5,
    dx: (Math.random() - 0.5) * 520,
    dy: (Math.random() - 0.5) * 600 + Math.random() * 120,
    rot: Math.random() * 1080 - 540,
    delay: Math.random() * 0.45,
    size: 36 + Math.floor(Math.random() * 44),
  }));
}

function readStoredUser(user) {
  try {
    return user || JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return user || {};
  }
}

function readThankYouUserId(user) {
  const stored = readStoredUser(user);
  const id = stored?.userId ?? stored?.userid ?? stored?.id;
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

export default function ThankYou() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  const storedUser = useMemo(() => readStoredUser(user), [user]);
  const viewerId = readThankYouUserId(user);
  const sessionId = storedUser?.sessionId;
  const organizationId = storedUser?.organizationId;

  const nav = location.state;
  const [points, setPoints] = useState(
    typeof nav?.points === "number" ? nav.points : 0
  );
  const [savedFeedback, setSavedFeedback] = useState("");
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [confettiOn, setConfettiOn] = useState(false);
  const [confettiEmoji, setConfettiEmoji] = useState("");
  const [confettiPieces, setConfettiPieces] = useState(null);

  const [pledges, setPledges] = useState([]);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [pledgesLoading, setPledgesLoading] = useState(false);
  const [likingId, setLikingId] = useState(null);
  const [step, setStep] = useState("pledge");

  useEffect(() => {
    if (user?.backButtonRedirect != null) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [user, dispatch]);

  useEffect(() => {
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    body.classList.remove("common-bg");
    body.style.backgroundImage = `url("${thankYouBg}")`;
    body.style.backgroundSize = "cover";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundPosition = "center center";
    body.style.backgroundAttachment = "fixed";
    return () => {
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, []);

  useEffect(() => {
    if (!viewerId || !adminToken || !backendBase || !sessionId || !organizationId) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: viewerId, sessionId, organizationId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !data.success || !data.report) return;
        const r = data.report;
        if (r.points !== undefined && r.points !== null) {
          setPoints(Number(r.points) || 0);
        }
        if (r.feedback != null && String(r.feedback).trim() !== "") {
          setSavedFeedback(String(r.feedback).trim());
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewerId, adminToken, backendBase, sessionId, organizationId]);

  const loadPledges = useCallback(
    async (nextPage = 0) => {
      if (!viewerId || !adminToken || !backendBase || !sessionId || !organizationId) {
        return;
      }
      setPledgesLoading(true);
      try {
        const res = await fetch(`${backendBase}/getPledges`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: viewerId,
            sessionId,
            organizationId,
            page: nextPage,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) return;
        setPledges(Array.isArray(data.pledges) ? data.pledges : []);
        setPage(Number(data.page) || 0);
        setPageCount(Number(data.pageCount) || 0);
      } catch (err) {
        console.error("loadPledges:", err);
      } finally {
        setPledgesLoading(false);
      }
    },
    [viewerId, adminToken, backendBase, sessionId, organizationId]
  );

  useEffect(() => {
    loadPledges(0);
  }, [loadPledges]);

  const submitFeedback = async (value) => {
    if (!viewerId || !adminToken || !backendBase || feedbackSaving || savedFeedback) {
      return;
    }
    setFeedbackError("");
    setFeedbackSaving(true);
    try {
      const res = await fetch(`${backendBase}/welcomeStageFeedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: viewerId,
          sessionId,
          organizationId,
          feedback: value,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setFeedbackError(data.message || "Could not save feedback. Please try again.");
        return;
      }
      setSavedFeedback(value);

      const opt = FEEDBACK_OPTIONS.find((o) => o.value === value);
      const emojiChar = opt?.emoji || "";
      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (emojiChar && !reduceMotion) {
        setConfettiEmoji(emojiChar);
        setConfettiPieces(buildEmojiConfettiPieces());
        setConfettiOn(true);
        window.setTimeout(() => {
          setConfettiOn(false);
          setConfettiPieces(null);
          setConfettiEmoji("");
          navigate("/leaderboard");
        }, CONFETTI_MS);
      } else {
        navigate("/leaderboard");
      }
    } catch (e) {
      setFeedbackError(e.message || "Could not save feedback.");
    } finally {
      setFeedbackSaving(false);
    }
  };

  const handleLike = async (pledge) => {
    if (!pledge || likingId != null) return;
    if (pledge.liked) {
      await Swal.fire({
        imageUrl: ohnoImg,
        imageWidth: 120,
        title: " ",
        confirmButtonText: "OK",
      });
      return;
    }

    setLikingId(pledge.id);
    try {
      const res = await fetch(`${backendBase}/likePledge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: viewerId,
          sessionId,
          organizationId,
          postId: pledge.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.alreadyLiked) {
        await Swal.fire({
          imageUrl: ohnoImg,
          imageWidth: 120,
          title: " ",
          confirmButtonText: "OK",
        });
        await loadPledges(page);
        return;
      }
      if (!res.ok || !data.success) {
        await Swal.fire({
          icon: "error",
          text: data.message || "Could not like this pledge.",
        });
        return;
      }
      setPledges((prev) =>
        prev.map((p) =>
          p.id === pledge.id
            ? { ...p, liked: true, likes: Number(data.likes) || p.likes + 1 }
            : p
        )
      );
      await Swal.fire({
        imageUrl: popupLikeImg,
        imageWidth: 140,
        title: "Thank you for liking!",
        confirmButtonText: "OK",
      });
    } catch (err) {
      console.error("handleLike:", err);
    } finally {
      setLikingId(null);
    }
  };

  const feedbackLocked = Boolean(savedFeedback) || feedbackSaving;
  const canSubmitFeedback = Boolean(viewerId && adminToken && backendBase);

  const localOwnPledge = useMemo(() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem("navratri_pledge") || "null");
      if (!raw || raw.pledgeId == null) return null;
      return {
        id: "local",
        pledgename: String(raw.pledgeId),
        ownpledgemsz: raw.ownPledge || "",
        likes: 0,
        liked: false,
        name:
          storedUser?.name ||
          [storedUser?.firstName, storedUser?.lastName].filter(Boolean).join(" ") ||
          "Player",
        isOwn: true,
      };
    } catch {
      return null;
    }
  }, [storedUser]);

  /** All pledges for this session (API), plus local own card if not saved yet. */
  const displayPledges = useMemo(() => {
    const list = Array.isArray(pledges) ? [...pledges] : [];
    const hasOwnInList = list.some((p) => p.isOwn);
    if (!hasOwnInList && localOwnPledge && page === 0) {
      return [localOwnPledge, ...list];
    }
    return list;
  }, [pledges, localOwnPledge, page]);

  const goToFeedback = () => {
    if (savedFeedback) {
      navigate("/leaderboard");
      return;
    }
    setStep("feedback");
  };

  const renderPledgeCard = (pledge) => (
    <article
      key={pledge.id}
      className={`thank-you-badge${pledge.isOwn ? " thank-you-badge--own" : ""}`}
    >
      <span className="thank-you-badge__icon" aria-hidden="true">
        {PLEDGE_ICONS[Number(pledge.pledgename)] ?? "✨"}
      </span>
      <div className="thank-you-badge__body">
        <p className="thank-you-badge__text">
          {pledgeDisplayText(pledge.pledgename, pledge.ownpledgemsz)}
        </p>
        <div className="thank-you-badge__meta">
          <button
            type="button"
            className="thank-you-badge__like"
            disabled={
              likingId != null ||
              pledge.id === "local" ||
              Boolean(pledge.isOwn)
            }
            onClick={() => handleLike(pledge)}
            title={
              pledge.isOwn
                ? "You cannot like your own pledge"
                : pledge.liked
                  ? "Already liked"
                  : "Like"
            }
          >
            <img
              src={pledge.liked ? likeOnImg : likeOffImg}
              alt={pledge.liked ? "Liked" : "Like"}
            />
            <span>{pledge.likes}</span>
          </button>
          <span className="thank-you-badge__name">
            -{pledge.name || "Player"}
          </span>
        </div>
      </div>
    </article>
  );

  return (
    <>
      {confettiOn && confettiEmoji && confettiPieces ? (
        <div className="thank-you-confetti" aria-hidden="true">
          {confettiPieces.map((p) => (
            <span
              key={p.key}
              className="thank-you-confetti-emoji"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                fontSize: p.size,
                "--ty-dx": `${p.dx}px`,
                "--ty-dy": `${p.dy}px`,
                "--ty-rot": `${p.rot}deg`,
                animationDelay: `${p.delay}s`,
              }}
            >
              {confettiEmoji}
            </span>
          ))}
        </div>
      ) : null}

      <div className="thank-you-page">
        <img src={thankYouTitle} alt="Thank You" className="thank-you-title-img" />

        {step === "pledge" ? (
          <>
            {pledgesLoading && displayPledges.length === 0 ? (
              <p className="thank-you-empty">Loading pledges…</p>
            ) : displayPledges.length > 0 ? (
              <>
                <div className="thank-you-badges">
                  {displayPledges.map(renderPledgeCard)}
                </div>
                {pageCount > 1 ? (
                  <div className="thank-you-pager" role="navigation" aria-label="Pledge pages">
                    <button
                      type="button"
                      disabled={page <= 0 || pledgesLoading}
                      onClick={() => loadPledges(page - 1)}
                    >
                      Prev
                    </button>
                    {Array.from({ length: pageCount }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={i === page ? "is-active" : undefined}
                        disabled={pledgesLoading}
                        onClick={() => loadPledges(i)}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={page >= pageCount - 1 || pledgesLoading}
                      onClick={() => loadPledges(page + 1)}
                    >
                      Next
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="thank-you-empty">No pledges found for this session.</p>
            )}

            <button
              type="button"
              className="thank-you-continue"
              onClick={goToFeedback}
            >
              Continue
            </button>
          </>
        ) : (
          <section
            className="thank-you-feedback"
            aria-label="How was your recipe experience? Tap an emoji to share your thoughts"
          >
            <h2 className="thank-you-feedback__title">
              How was your recipe experience? Tap an emoji to share your thoughts 🥗😊
            </h2>
            <div className="thank-you-feedback__grid">
              {FEEDBACK_OPTIONS.map((opt) => {
                const selected = savedFeedback === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`thank-you-feedback__card${
                      selected ? " thank-you-feedback__card--selected" : ""
                    }`}
                    onClick={() => submitFeedback(opt.value)}
                    disabled={!canSubmitFeedback || feedbackLocked}
                    aria-pressed={selected}
                  >
                    <span className="thank-you-feedback__emoji" aria-hidden="true">
                      {opt.emoji}
                    </span>
                    <span className="thank-you-feedback__label">{opt.label}</span>
                  </button>
                );
              })}
            </div>
            {feedbackError ? (
              <p className="thank-you-feedback__error">{feedbackError}</p>
            ) : null}
            <p className="thank-you-feedback__hint">Your feedback helps us improve!</p>
          </section>
        )}
      </div>
    </>
  );
}
